import React, { useEffect, useRef, useState } from 'react'
import { notify, useFetch, usePost } from '../../../components/component-items/helpers'
import { globalState } from '../../../store'

// Components
import GlobalStyles from '../../../components/component-items/styles'
import GenericModal from '../../../components/component-items/modal'
import Form from 'react-bootstrap/Form'
import { TableCell } from '../../../components/component-items/datatable'

// Plugins
import styled from 'styled-components'
import { useParams } from 'react-router-dom'
import CardLoading from '../../../components/component-items/loading-popover'
import { faPrint } from '@fortawesome/pro-duotone-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { LargeButton } from '../../../components/component-items/buttons'

type SSCCBarcodeModalProps = {
  open: boolean
  setOpen: (open: boolean) => void
}

export const TableRow = styled(GlobalStyles.TableRow)`
  p {
    max-width: fit-content;
    margin-bottom: 0;
  }
`

const Tooltip = styled(GlobalStyles.Tooltip)`
  &:before {
    width: 100px;
    left: 50%;
    transform: translate(-50%, 0);
  }
`

export const SSCCBarcodeModal = ({ open, setOpen }: SSCCBarcodeModalProps) => {
  const { id: order } = useParams<{ id: string }>()
  const {
    state: { csrf, userIsWarehouse },
  } = globalState()

  const node = useRef<HTMLFormElement | null>(null)
  const [validated, setValidated] = useState(false)
  const [handlingUnits, setHandlingUnits] = useState<any>([])

  const hasOrderParam = Boolean(order)
  const shouldFetchHandlingUnits = open && hasOrderParam
  const url = shouldFetchHandlingUnits ? `/api/order/${order}/shipments/` : ''
  const { response: resp, loaded, error, placeholder }: any = useFetch(url, {})

  const notifyMissingOrderParam = () => {
    notify({
      type: 'error',
      title: 'SSCC Barcodes',
      message: 'Order route parameter is missing. Please refresh and try again.',
    })
  }

  useEffect(() => {
    if (open && !hasOrderParam) {
      setHandlingUnits([])
      return
    }

    if (loaded) {
      setHandlingUnits(resp?.handling_units || [])
    }
  }, [open, hasOrderParam, loaded, resp])

  const handleSaveBarcodes = async (event: any) => {
    event.preventDefault()
    event.stopPropagation()
    if (handlingUnits.some((handlingUnit: any) => handlingUnit.ssccBarcode.length != 20)) {
      notify({
        type: 'error',
        title: 'SSCC Barcodes',
        message: 'SSCC Barcodes must have a length of 20 characters.',
      })
      return
    }

    let updatedHandlingUnits = {} as any
    handlingUnits.map((handlingUnit: any) => {
      updatedHandlingUnits[handlingUnit.handlingUnitNumber] = handlingUnit.ssccBarcode
    })

    setValidated(true)
    if (node?.current?.checkValidity() === false) {
      return
    }

    if (!hasOrderParam) {
      notifyMissingOrderParam()
      return
    }

    const resp = await usePost(
      `/api/order/${order}/create_manual_sscc_barcodes/`,
      { handling_units: updatedHandlingUnits },
      csrf,
      false
    )
    if (!resp.error) {
      notify({
        type: 'success',
        title: 'SSCC Barcodes',
        message: 'SSCC Barcodes have been successfully updated for this order.',
      })
      setOpen(false)
    }
  }

  const handleGenerateBarcodes = async () => {
    if (!hasOrderParam) {
      notifyMissingOrderParam()
      return
    }

    const resp = await usePost(`/api/order/${order}/edi_generate_sscc_barcodes/`, {}, csrf, false)
    if (resp?.success) {
      notify({
        type: 'success',
        title: 'SSCC Barcodes',
        message: 'SSCC Barcodes have been successfully generated for this order.',
      })
      setOpen(false)
      window.open(`/api/order/${order}/print_gs1_labels/`, '_blank')
    }
  }

  const printBarcode = (id: number) => {
    window.open(`/api/shipment/${id}/print_sscc_barcode/`, '_blank')
  }

  const printBarcodes = () => {
    if (!hasOrderParam) {
      notifyMissingOrderParam()
      return
    }

    window.open(`/api/order/${order}/print_gs1_labels/`, '_blank')
  }

  return (
    <GenericModal
      heading={'Edit SSCC Barcodes'}
      show={open}
      size="lg"
      onHide={() => setOpen(false)}
      buttons={
        <>
          <GlobalStyles.Button className="secondary" style={{ minWidth: 125 }} onClick={handleGenerateBarcodes}>
            Generate Barcodes
          </GlobalStyles.Button>
          {userIsWarehouse && (
            <GlobalStyles.Button className="royal" style={{ minWidth: 125 }} onClick={() => printBarcodes()}>
              Print Barcodes
            </GlobalStyles.Button>
          )}
          <GlobalStyles.Button style={{ minWidth: 125 }} onClick={handleSaveBarcodes}>
            Save Barcodes
          </GlobalStyles.Button>
        </>
      }
    >
      <Form noValidate ref={node} validated={validated} onSubmit={handleSaveBarcodes} style={{ minHeight: 400 }}>
        <GlobalStyles.DataTable>
          <thead>
            <tr>
              <th>
                <span className="text center">Handling Unit</span>
              </th>
              <th>
                <span className="text center">Order Items</span>
              </th>
              <th>
                <span className="text center">Dims</span>
              </th>
              <th>
                <span className="text center">SSCC Barcode</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {handlingUnits?.map((handlingUnit: any, id: number) => (
              <GlobalStyles.TableRow key={id}>
                <TableCell input={handlingUnit.handlingUnitNumber} center></TableCell>
                <TableCell>
                  {handlingUnit.orderItems?.products?.length && (
                    <div
                      style={{
                        maxHeight: 200,
                        overflow: 'scroll',
                        boxShadow: 'rgba(67, 56, 93, 0.09) -4px 4px 15px 1px',
                      }}
                    >
                      <GlobalStyles.DataTable>
                        <tbody>
                          {handlingUnit.orderItems?.products?.map((item: any, id: number) => (
                            <TableRow key={id}>
                              <TableCell input={item.sku}>
                                <div>{item.desc}</div>
                              </TableCell>
                              <TableCell input={item.qty} center />
                            </TableRow>
                          ))}
                        </tbody>
                      </GlobalStyles.DataTable>
                    </div>
                  )}
                </TableCell>
                <TableCell
                  center
                  input={`${handlingUnit.dims?.length} x ${handlingUnit.dims?.width} x ${handlingUnit.dims?.height} (${handlingUnit.dims?.weight} oz)`}
                ></TableCell>
                <TableCell>
                  <Form.Control
                    type="text"
                    placeholder="SSCC Barcode"
                    value={handlingUnit.ssccBarcode}
                    onChange={(e) => {
                      setHandlingUnits((handlingUnits: any) => {
                        return handlingUnits.map((s: any) => {
                          if (s.handlingUnitNumber === handlingUnit.handlingUnitNumber) {
                            s.ssccBarcode = e.target.value
                          }
                          return s
                        })
                      })
                    }}
                  />
                </TableCell>
                <TableCell
                  center
                  input={
                    <div className="dropdown__container" style={{ display: 'flex', justifyContent: 'center' }}>
                      <>
                        <Tooltip className="subheader__button button" data-title="Print Label">
                          <LargeButton type="button" onClick={() => printBarcode(handlingUnit.id)}>
                            <FontAwesomeIcon icon={faPrint} />
                          </LargeButton>
                        </Tooltip>
                      </>
                    </div>
                  }
                />
              </GlobalStyles.TableRow>
            ))}
          </tbody>
        </GlobalStyles.DataTable>
      </Form>
      {shouldFetchHandlingUnits && !loaded ? <CardLoading text={placeholder} error={error} /> : null}
    </GenericModal>
  )
}
